import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchAvailability, monthRange } from '../lib/appointments';
import { useLang, t, getLocale } from '../context/LanguageContext';
import translations from '../i18n/translations';

// Headers jour pour 6 langues (ISO Mon-Sun). Fallback FR si lang inconnue.
const DAY_NAMES_HEADER = {
  fr: ['L', 'M', 'M', 'J', 'V', 'S', 'D'],
  en: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
  es: ['L', 'M', 'X', 'J', 'V', 'S', 'D'],
  de: ['M', 'D', 'M', 'D', 'F', 'S', 'S'],
  it: ['L', 'M', 'M', 'G', 'V', 'S', 'D'],
  pt: ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'],
};

export default function AppointmentCalendar({ onSelectSlot, typeAlias = 'comptoir', durationMin = null }) {
  const { lang } = useLang();
  const tc = translations.calendar;
  const dayNames = DAY_NAMES_HEADER[lang] || DAY_NAMES_HEADER.fr;
  const locale = getLocale(lang);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    const { from, to } = monthRange(month);
    setLoading(true);
    setError(null);
    setSelectedDate(null);
    fetchAvailability(from, to, typeAlias, lang, durationMin)
      .then((d) => setDays(d.days || {}))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [month, typeAlias, lang, durationMin]);

  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const firstWeekday = (new Date(month.getFullYear(), month.getMonth(), 1).getDay() + 6) % 7;

  const goPrev = () => {
    const x = new Date(month);
    x.setMonth(x.getMonth() - 1);
    setMonth(x);
    setSelectedDate(null);
  };
  const goNext = () => {
    const x = new Date(month);
    x.setMonth(x.getMonth() + 1);
    setMonth(x);
    setSelectedDate(null);
  };

  return (
    <div data-testid="appointment-calendar">
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={goPrev}
          className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
          aria-label={t(tc.prev_month, lang)}
          data-testid="cal-prev"
        >
          <ChevronLeft size={20} />
        </button>
        <strong className="text-base md:text-lg font-semibold uppercase tracking-wider text-white">
          {month.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
        </strong>
        <button
          type="button"
          onClick={goNext}
          className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
          aria-label={t(tc.next_month, lang)}
          data-testid="cal-next"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-400/30 rounded-lg text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-7 gap-1.5 sm:gap-2 text-center text-sm">
        {dayNames.map((d, i) => (
          <div key={i} className="font-semibold text-slate-500 py-2 text-xs uppercase tracking-wider">
            {d}
          </div>
        ))}
        {Array.from({ length: firstWeekday }).map((_, i) => (
          <div key={`pad${i}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const dStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const slots = days[dStr] || [];
          const hasSlots = slots.length > 0;
          const isSelected = selectedDate === dStr;
          return (
            <button
              key={day}
              type="button"
              disabled={!hasSlots}
              onClick={() => setSelectedDate(dStr)}
              className={[
                'aspect-square rounded-lg text-sm font-medium transition-all',
                isSelected
                  ? 'bg-[#0099D6] text-white border border-[#0099D6] shadow-[0_0_15px_rgba(0,153,214,0.4)]'
                  : hasSlots
                    ? 'bg-[#0099D6]/10 text-[#0099D6] hover:bg-[#0099D6]/20 border border-[#0099D6]/20 hover:border-[#0099D6]/40'
                    : 'bg-white/[0.02] text-slate-600 border border-transparent cursor-not-allowed',
              ].join(' ')}
              data-testid={hasSlots ? `cal-day-${dStr}` : undefined}
            >
              {day}
            </button>
          );
        })}
      </div>

      {loading && (
        <p className="text-center mt-5 text-slate-400 text-sm" data-testid="cal-loading">
          {t(tc.loading_availability, lang)}
        </p>
      )}

      {selectedDate && days[selectedDate] && (
        <div className="mt-8" data-testid="slots-list">
          <h4 className="font-semibold text-base md:text-lg mb-4 text-white">
            {t(tc.slots_available_on, lang)}{' '}
            <span className="text-[#0099D6]">
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString(locale, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </span>
          </h4>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {days[selectedDate].map((slot) => (
              <button
                key={slot.start}
                type="button"
                onClick={() => onSelectSlot(slot)}
                className="py-3 px-2 bg-white/5 border border-white/10 rounded-lg text-sm font-semibold text-white hover:bg-[#0099D6] hover:text-white hover:border-[#0099D6] transition-all hover:scale-105 active:scale-95"
                data-testid={`slot-${slot.start}`}
              >
                {new Date(slot.start).toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedDate && (!days[selectedDate] || days[selectedDate].length === 0) && (
        <p className="text-center mt-5 text-slate-400 text-sm">
          {t(tc.no_slots_today, lang)}
        </p>
      )}
    </div>
  );
}
